import { createContext, type ReactNode, useContext } from "react";
import type { RowData } from "@tanstack/react-table";
import type { GridController } from "./focus/gridController";
import type {
    DataGridDensity,
    DataGridStatus,
    DataGridInstance,
} from "./types";

/** Receives the root's instance with its row type kept generic. */
export type DataGridInstanceVisitor<TResult> = <TData extends RowData>(
    instance: DataGridInstance<TData>,
) => TResult;

/**
 * What the parts read from the root. Not generic: parts that need the table
 * call `visit`, which hands them the instance with its row type intact.
 */
export interface DataGridContextValue {
    status: DataGridStatus;
    density: DataGridDensity;
    noun: string;
    hasActiveFilters: boolean;
    /** Ready with zero rows to show. */
    isEmpty: boolean;
    clearFilters: () => void;
    controller: GridController;
    rootElement: HTMLDivElement | null;
    /** Where the empty and error messages render: under the header, over the empty viewport. */
    messageLayer: HTMLDivElement | null;
    renderGrid: () => ReactNode;
    visit: <TResult>(visitor: DataGridInstanceVisitor<TResult>) => TResult;
}

export const DataGridContext = createContext<DataGridContextValue | null>(null);

export const useDataGridContext = (): DataGridContextValue => {
    const value = useContext(DataGridContext);
    if (!value) {
        throw new Error("DataGrid parts must render inside <DataGrid>");
    }
    return value;
};
